import React from "react";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    TouchSensor,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { Modal, Button } from "antd";
import { useTranslation } from "react-i18next";

interface Item {
    id: number;
    name: string;
    color: string;
    position: number;
}

interface Props {
    open: boolean;
    data: Item[];
    onCancel: () => void;
    onSave: (newData: Item[]) => void;
}

function SortableItem({ item }: { item: Item }) {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="flex items-center gap-4 mb-3 mt-3"
        >
            {/* Drag Handle */}
            <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing touch-none select-none"
            >
                <div className="w-8 h-8 flex items-center justify-center text-gray-500 dark:text-gray-400">
                    ☰
                </div>
            </div>

            {/* Position circle */}
            <div className="w-10 h-10 flex items-center justify-center rounded-full border dark:border-gray-600 text-gray-700 t-text-white">
                {item.id}
            </div>

            {/* Colored label */}
            <div
                className="flex-1 py-2 rounded-full text-center text-white font-medium"
                style={{ backgroundColor: item.color }}
            >
                {item.name}
            </div>
        </div>
    );
}

export default function ChangePositionModal({
    open,
    data,
    onCancel,
    onSave,
}: Props) {
    const { t } = useTranslation();
    const [items, setItems] = React.useState<Item[]>([]);

    React.useEffect(() => {
        setItems(data);
    }, [data]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 200,
                tolerance: 5,
            },
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setItems((prev) => {
        const oldIndex = prev.findIndex((i) => i.id === active.id);
        const newIndex = prev.findIndex((i) => i.id === over.id);

        const newArr = [...prev];

        // 👉 swap position
        const temp = newArr[oldIndex].position;
        newArr[oldIndex].position = newArr[newIndex].position;
        newArr[newIndex].position = temp;

        // 👉 vẫn cần reorder UI
        return arrayMove(newArr, oldIndex, newIndex);
    });
};

    return (
        <Modal
            title={t('care_status.change_position_title')}
            open={open}
            footer={null}
            onCancel={onCancel}
            centered
            destroyOnClose
            className="
                dark:[&_.ant-modal-content]:bg-gray-800
                dark:[&_.ant-modal-title]:text-white
                dark:[&_.ant-modal-close]:text-white
                dark:[&_.ant-modal-header]:!bg-transparent
            "
        >
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={items.map((i) => i.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {items.map((item) => (
                        <SortableItem key={item.id} item={item} />
                    ))}
                </SortableContext>
            </DndContext>

            <div className="flex justify-end gap-2 pt-4 border-t dark:border-gray-700">
                <Button onClick={onCancel}>{t('care_status.cancel')}</Button>
                <Button
                    type="primary"
                    className="!bg-[#3e91ab] border-none"
                    onClick={() => onSave(items)}
                >
                    OK
                </Button>
            </div>
        </Modal>
    );
}